<template>
  <b-card class="claimed-rewards-card" no-body>
    <b-card-header class="py-3 d-flex justify-content-between align-items-center">
      <h4 class="mb-0"><strong>Claimed Rewards</strong></h4>
    </b-card-header>
    <b-card-body>
      <div v-if="!claimedRewardItems || claimedRewardItems.length === 0" class="py-4 d-flex justify-content-center">
        <span class="text-muted">You don't have any claimed rewards yet.</span>
      </div>
      <template v-else>
        <b-alert v-if="!isAdminView" variant="info" show><b>If you have any questions related to your
          claimed rewards please <b-link v-b-modal.reward-delivery-description>click here</b-link>.</b></b-alert>
        <div
          v-for="{ id, updated, created, tokenCost, status, statusBackground, title, imageURL } in claimedRewardItems"
          class="d-flex w-100 justify-content-between border-bottom py-3"
          :key="id"
        >
          <div class="d-flex w-100">
            <b-img :src="imageURL" v-bind="{ width: 94, height: 70 }" rounded></b-img>
            <div class="d-flex flex-fill flex-column pl-2">
              <div class="d-flex flex-grow justify-content-between align-items-center">
                <h4 class="mb-0"><strong>{{ title }}</strong></h4>
                <span :style="{ backgroundColor: statusBackground }" class="status-badge">
                  <strong>{{ status }}</strong>
                </span>
              </div>
              <div class="d-flex justify-content-between">
                <div style="font-size: 13px">
                  <span>Claimed at: <strong>{{ created }}</strong></span><br>
                  <span>Updated at: <strong>{{ updated }}</strong></span>
                </div>
                <div class="py-2"><div class="token-badge">{{ tokenCost }} <rewards-coin /></div></div>
              </div>
            </div>
          </div>
        </div>
        <div class="d-flex flex-column justify-content-center align-items-center">
          <b-pagination
            v-if="showAllItems && (claimedRewards || []).length > perPage"
            class="mb-0 mt-3"
            v-model="currentPage"
            :total-rows="(claimedRewards || []).length"
            :per-page="perPage"
            size="sm"
          ></b-pagination>
          <b-link
            class="mt-3"
            v-if="(claimedRewards || []).length > 1"
            @click="showAllItems = !showAllItems"
          >Show {{ showAllItems ? 'Less' : 'More' }}</b-link>
        </div>
      </template>
    </b-card-body>
    <b-modal id="reward-delivery-description" title="Reward Delivery FAQ" size="lg">
      <h4><b>The status of my reward is still 'Requested' even though I claimed it a few days ago</b></h4>
      <p>All reward claims need to be accepted by an administrator. If the status of your claim is 'Requested' for a long time please
        <a v-if="isEXPD8" href="mailto:rewards@expd8.co.uk">contact rewards@expd8.co.uk</a><span v-else>contact your recruitment team</span>, they can accept or reject your claim.</p>
      <h4><b>The status of my reward is 'Fulfilled' but I have not received it yet</b></h4>
      <p class="mb-0">Some rewards can take up to two weeks to be delivered. If you have any questions about when you can expect it please
        <a href="mailto:cs@xoxoday.com">contact cs@xoxoday.com</a>. Let them know about the following details so they can help you.</p>
      <ul>
        <li>The company you work at</li>
        <li>Your name and work email address</li>
        <li>The name of the voucher you claimed</li>
        <li>The date and time it was requested at</li>
      </ul>
      <h4><b>Why is the status of my claim 'Rejected'?</b></h4>
      <p>If you do not understand why your claim got rejected please <a v-if="isEXPD8" href="mailto:rewards@expd8.co.uk">contact rewards@expd8.co.uk</a><span v-else>contact your recruitment team</span>.</p>
    </b-modal>
  </b-card>
</template>

<script>
import distanceInWords from 'date-fns/distance_in_words';
import { RewardsCoin } from '@src/components/Icons';
import { mapRoot } from '@/mixins/root';

export default {
  components: {
    RewardsCoin,
  },
  props: {
    claimedRewards: {
      type: Array,
      default: () => null,
    },
    isAdminView: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      perPage: 5,
      currentPage: 1,
      showAllItems: false,
    };
  },
  computed: {
    ...mapRoot([ 'isEXPD8' ]),
    claimedRewardItems() {
      const { claimedRewards, perPage, currentPage, showAllItems } = this;
      console.log({ claimedRewards });
      const statusBackgroundMap = {
        REQUESTED: '#ebf2ff',
        VERIFIED: '#ebf2ff',
        APPROVED: '#edffeb',
        FULFILLED: '#edffeb',
        REJECTED: '#ffebeb'
      };
      return (Array.isArray(claimedRewards) ? claimedRewards : [])
        .map(({ id, updatedAt, createdAt, tokenCost, status, rewardData, reward }) => {
          const { title, imageURL, image } = rewardData || reward || {};
          const item = {
            id,
            title: this.ellipsis(title || 'Unknown Reward'),
            created: this.timeAgo(createdAt),
            updated: this.timeAgo(updatedAt),
            tokenCost,
            status,
            statusBackground: statusBackgroundMap[status] || '#fff',
            imageURL: imageURL || (image ? image.fileLocation : null) || null,
          };
          return item;
        })
        .slice(showAllItems ? (currentPage - 1) * perPage : 0, showAllItems ? currentPage * perPage : 1);
    },
  },
  methods: {
    timeAgo(createdAt) {
      return distanceInWords(new Date(), createdAt, { addSuffix: true })
        .replace('about ', '')
        .replace('less than a minute ago', '1m ago')
        .replace(' seconds', 's')
        .replace(' second', 's')
        .replace(' minutes', 'm')
        .replace(' minute', 'm')
        .replace(' hours', 'h')
        .replace(' hour', 'h')
        .replace(' days', 'd')
        .replace(' day', 'd')
        .replace(' weeks', 's')
        .replace(' week', 's')
        .replace(' months', 'mo')
        .replace(' month', 'mo')
        .replace(' years', 'y')
        .replace(' year', 'y');
    },
    ellipsis(text, length = 30) {
      return text.length <= length ? text : `${text.substring(0, length)}...`;
    }
  },
};
</script>

<style lang="scss">
.claimed-rewards-card {
  .status-badge {
    display: inline-block;
    padding: 6px 12px;
    font-size: 13px;
    border-radius: 3px;
    font-weight: bold;
  }
  .token-badge {
    display: inline-block;
    padding: 4px 8px;
    font-size: 13px;
    border-radius: 3px;
    font-weight: bold;
    background-color: #f4f5f8;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/rewards/ClaimedRewards.vue